// WeatherFx — instanced particle systems for rain / snow / leaves / dust.
// Uses instanced meshes for perf. Auto-recycles particles as they fall
// below y=0.
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const N_RAIN = 700;
const N_SNOW = 500;
const N_LEAVES = 60;
const N_DUST = 80;

export default function WeatherFx({ weather, arena }) {
  const rainy = weather === 'rain' || weather === 'storm';
  const snowy = weather === 'snow' || arena === 'tundra' || arena === 'snow';
  const misty = weather === 'fog';
  const leafy = arena === 'forest' || arena === 'meadow' || arena === 'wetlands';

  return (
    <>
      {rainy && <Rain count={weather === 'storm' ? N_RAIN : Math.floor(N_RAIN * 0.6)} />}
      {snowy && <Snow count={N_SNOW} />}
      {leafy && !rainy && <Leaves count={N_LEAVES} />}
      {!rainy && !snowy && !misty && <Dust count={N_DUST} />}
      {misty && <FogPlanes />}
    </>
  );
}


function Rain({ count }) {
  const mesh = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const positions = useMemo(() =>
    Array.from({ length: count }, () => ({
      x: (Math.random() - 0.5) * 60,
      y: Math.random() * 30 + 6,
      z: (Math.random() - 0.5) * 40,
      speed: 24 + Math.random() * 10,
    })), [count]);

  useFrame((_, dt) => {
    if (!mesh.current) return;
    for (let i = 0; i < count; i++) {
      const p = positions[i];
      p.y -= p.speed * dt;
      if (p.y < -1) { p.y = 24 + Math.random() * 5; p.x = (Math.random() - 0.5) * 60; }
      dummy.position.set(p.x, p.y, p.z);
      dummy.rotation.set(0, 0, 0.15);
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    }
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]}>
      <boxGeometry args={[0.015, 0.45, 0.015]} />
      <meshBasicMaterial color="#9fc9ff" transparent opacity={0.55} />
    </instancedMesh>
  );
}


function Snow({ count }) {
  const mesh = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const flakes = useMemo(() =>
    Array.from({ length: count }, () => ({
      x: (Math.random() - 0.5) * 60,
      y: Math.random() * 24 + 4,
      z: (Math.random() - 0.5) * 40,
      sway: Math.random() * Math.PI * 2,
      speed: 1.4 + Math.random() * 1.2,
    })), [count]);

  useFrame((_, dt) => {
    if (!mesh.current) return;
    for (let i = 0; i < count; i++) {
      const f = flakes[i];
      f.y -= f.speed * dt;
      f.sway += dt * 1.2;
      if (f.y < -0.2) { f.y = 22 + Math.random() * 6; f.x = (Math.random() - 0.5) * 60; }
      dummy.position.set(f.x + Math.sin(f.sway) * 0.25, f.y, f.z);
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    }
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]}>
      <sphereGeometry args={[0.05, 6, 6]} />
      <meshBasicMaterial color="#ffffff" transparent opacity={0.85} />
    </instancedMesh>
  );
}


function Leaves({ count }) {
  const mesh = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const leaves = useMemo(() =>
    Array.from({ length: count }, () => ({
      x: (Math.random() - 0.5) * 30,
      y: Math.random() * 12 + 3,
      z: (Math.random() - 0.5) * 25,
      sway: Math.random() * Math.PI * 2,
      speed: 0.9 + Math.random() * 0.9,
      rot: Math.random() * Math.PI,
    })), [count]);

  useFrame((_, dt) => {
    if (!mesh.current) return;
    for (let i = 0; i < count; i++) {
      const f = leaves[i];
      f.y -= f.speed * dt;
      f.sway += dt * 2.4;
      f.rot += dt * 1.5;
      if (f.y < -0.1) { f.y = 10 + Math.random() * 4; f.x = (Math.random() - 0.5) * 30; }
      dummy.position.set(f.x + Math.sin(f.sway) * 0.6, f.y, f.z + Math.cos(f.sway * 0.7) * 0.5);
      dummy.rotation.set(f.rot, f.rot * 0.7, f.rot * 0.5);
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    }
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]}>
      <planeGeometry args={[0.16, 0.24]} />
      <meshBasicMaterial color="#c48a3b" side={THREE.DoubleSide} transparent opacity={0.9} />
    </instancedMesh>
  );
}


function Dust({ count }) {
  const mesh = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const motes = useMemo(() =>
    Array.from({ length: count }, () => ({
      x: (Math.random() - 0.5) * 12,
      y: Math.random() * 3 + 0.4,
      z: (Math.random() - 0.5) * 8,
      sway: Math.random() * Math.PI * 2,
      speed: 0.25 + Math.random() * 0.3,
    })), [count]);

  useFrame((_, dt) => {
    if (!mesh.current) return;
    for (let i = 0; i < count; i++) {
      const f = motes[i];
      f.sway += dt * 0.6;
      f.y += Math.sin(f.sway) * 0.003;
      f.x += Math.cos(f.sway * 0.7) * 0.008;
      dummy.position.set(f.x, f.y, f.z);
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    }
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]}>
      <sphereGeometry args={[0.028, 4, 4]} />
      <meshBasicMaterial color="#ffd48a" transparent opacity={0.65} />
    </instancedMesh>
  );
}


function FogPlanes() {
  const planes = useMemo(() =>
    Array.from({ length: 6 }, (_, i) => ({ pos: [((i - 3) * 3.5), 0.6 + i * 0.05, -2 - i * 0.4] })),
  []);
  return planes.map((p, i) => (
    <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={p.pos}>
      <planeGeometry args={[16, 6]} />
      <meshBasicMaterial color="#dfe6ee" transparent opacity={0.09} depthWrite={false} />
    </mesh>
  ));
}
